// Encryption utilities for URL parameters
// Uses AES encryption with a secret key
// MUST match the Python version exactly for compatibility

const SECRET_KEY = '9a2b7c4d1e5f80316789a1b2c3d4e5f60718293a4b5c6d7e8f90123456789abc'; // 64 hex chars = 32 bytes for AES-256
const SEPARATOR = '$@$';

/**
 * Encrypts data using AES encryption
 * @param data - The data to encrypt
 * @returns Promise<string> - Encrypted string in base64 format
 */
export const encrypt = async (data: string): Promise<string> => {
  try {
    // Convert the hex secret key to a Uint8Array (32 bytes for AES-256)
    const keyData = new Uint8Array(SECRET_KEY.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
    
    // Generate a random IV (Initialization Vector) - 12 bytes for GCM
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // Import the key
    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );
    
    // Encrypt the data
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(data)
    );
    
    // Extract the encrypted data and tag
    const encryptedArray = new Uint8Array(encrypted);
    // For GCM, the last 16 bytes are the authentication tag
    const encryptedData = encryptedArray.slice(0, -16);
    const tag = encryptedArray.slice(-16);
    
    // Combine IV, encrypted data, and tag (exactly like Python version)
    const combined = new Uint8Array(iv.length + encryptedData.length + tag.length);
    combined.set(iv, 0);
    combined.set(encryptedData, iv.length);
    combined.set(tag, iv.length + encryptedData.length);
    
    // Convert to base64
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Encryption failed');
  }
};

/**
 * Decrypts data using AES decryption
 * @param encryptedData - The encrypted data to decrypt
 * @returns Promise<string> - Decrypted string
 */
export const decrypt = async (encryptedData: string): Promise<string> => {
  try {
    // Convert from base64
    const combined = new Uint8Array(
      atob(encryptedData).split('').map(char => char.charCodeAt(0))
    );
    
    // Extract IV, encrypted data, and tag (exactly like Python version)
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12, -16);
    const tag = combined.slice(-16);
    
    // Convert the hex secret key to a Uint8Array (32 bytes for AES-256)
    const keyData = new Uint8Array(SECRET_KEY.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
    
    // Import the key
    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    
    // Combine encrypted data and tag for decryption
    const encryptedWithTag = new Uint8Array(encrypted.length + tag.length);
    encryptedWithTag.set(encrypted, 0);
    encryptedWithTag.set(tag, encrypted.length);
    
    // Decrypt the data
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encryptedWithTag
    );
    
    // Convert back to string
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Decryption failed');
  }
};

/**
 * Encrypts multiple values and combines them with a separator
 * @param values - Array of values to encrypt
 * @returns Encrypted string with all values
 */
export const encryptMultiple = async (values: string[]): Promise<string> => {
  const combined = values.join(SEPARATOR);
  return await encrypt(combined);
};

/**
 * Decrypts a combined string and splits it into multiple values
 * @param encryptedData - The encrypted data containing multiple values
 * @returns Array of decrypted values
 */
export const decryptMultiple = async (encryptedData: string): Promise<string[]> => {
  const decrypted = await decrypt(encryptedData);
  return decrypted.split(SEPARATOR);
};

/**
 * Creates an encrypted URL parameter for registration
 * @param email - User's email
 * @param inviteCode - Invitation code
 * @param companyId - Company ID
 * @param role - User's role
 * @returns Encrypted parameter string
 */
export const createEncryptedRegistrationParam = async (
  email: string,
  inviteCode: string,
  companyId: string,
  role: string
): Promise<string> => {
  return await encryptMultiple([email, inviteCode, companyId, role]);
};

/**
 * Decrypts registration parameters from URL
 * @param encryptedParam - The encrypted parameter from URL
 * @returns Object with email, inviteCode, companyId, and role
 */
export const decryptRegistrationParams = async (encryptedParam: string): Promise<{
  email: string;
  inviteCode: string;
  companyId: string;
  role: string;
}> => {
  const [email, inviteCode, companyId, role] = await decryptMultiple(encryptedParam);
  return { email, inviteCode, companyId, role };
};

/**
 * Creates a registration URL with encrypted parameters
 * @param baseUrl - Base URL for registration
 * @param email - User's email
 * @param inviteCode - Invitation code
 * @param companyId - Company ID
 * @param role - User's role
 * @returns Complete registration URL
 */
export const createRegistrationUrl = async (
  baseUrl: string,
  email: string,
  inviteCode: string,
  companyId: string,
  role: string
): Promise<string> => {
  const encryptedParam = await createEncryptedRegistrationParam(email, inviteCode, companyId, role);
  return `${baseUrl}/register?e=${encodeURIComponent(encryptedParam)}`;
};

// Test functions for development
export const testEncryption = async () => {
  const testData = {
    email: 'kana@giggso.com',
    inviteCode: 'MpU69ugwzG69r1PCQjvpYPokZQv9HeNt8y9mO2oNYts',
    companyId: '293792874982-242947924-242342344',
    role: 'user'
  };


  try {
    // Test encryption
    const encrypted = await createEncryptedRegistrationParam(
      testData.email,
      testData.inviteCode,
      testData.companyId,
      testData.role
    );

    // Test decryption
    const decrypted = await decryptRegistrationParams(encrypted);

    // Test URL creation
    const url = await createRegistrationUrl(
      'http://localhost:3000',
      testData.email,
      testData.inviteCode,
      testData.companyId,
      testData.role
    );

    return { encrypted, decrypted, url };
  } catch (error) {
    console.error('Test failed:', error);
    throw error;
  }
};
